import { NativePromptConfig } from '@pushly/models/lib/structs/prompts/styles/native/native-prompt-config'
import { PromptBehavior } from '@pushly/models/lib/structs/prompts/prompt-behavior'
import { SlidePromptConfig } from '@pushly/models/lib/structs/prompts/styles/slide/slide-prompt-config'
import {
    CreateCustomPromptRequest,
    CustomPromptConfig,
    GetAbstractPromptResponse,
    GetCustomPromptResponse,
    GetNativePromptResponse,
    GetSlidePromptResponse,
    isGetCustomPromptResponse,
    isUpdateCustomPromptRequest,
    NativePromptTheme,
    PromptButtonConfig,
    PromptDevicePosition,
    SlidePromptTheme,
    UpdateCustomPromptRequest,
} from '@pushly/models/lib/structs'
import { PromptStyle, SlidePromptLayout } from '@pushly/models/lib/enums'
import { CreateAbstractPromptRequest } from '@pushly/models/lib/structs/prompts/styles/create-abstract-prompt-request'
import { UpdateAbstractPromptRequest } from '@pushly/models/lib/structs/prompts/styles/update-abstract-prompt-request'
import {
    CreateSlidePromptRequest,
    isCreateSlidePromptRequest,
    isGetSlidePromptResponse,
    isUpdateSlidePromptRequest,
    UpdateSlidePromptRequest,
} from '@pushly/models/lib/structs/prompts/styles/slide/slide-prompt'
import {
    CreateNativePromptRequest,
    isCreateNativePromptRequest,
    isGetNativePromptResponse,
    isUpdateNativePromptRequest,
    UpdateNativePromptRequest,
} from '@pushly/models/lib/structs/prompts/styles/native/native-prompt'
import { isCreateCustomPromptRequest } from '@pushly/models/lib/structs/prompts/styles/custom/custom-prompt'
import { PromptTextConfig } from '@pushly/models/lib/structs/prompts/prompt-text-config'
import {
    DEFAULT_ALLOW_BUTTON_COLOR,
    DEFAULT_ALLOW_TEXT,
    DEFAULT_ALLOW_TEXT_COLOR,
    DEFAULT_BACKGROUND_COLOR,
    DEFAULT_DESKTOP_POSITION,
    DEFAULT_DISMISS_BUTTON_COLOR,
    DEFAULT_DISMISS_TEXT,
    DEFAULT_DISMISS_TEXT_COLOR,
    DEFAULT_LAYOUT,
    DEFAULT_MOBILE_POSITION,
    DEFAULT_SUBTITLE_COLOR,
    DEFAULT_TITLE_COLOR,
    VARIANT_NAME_MAX_CHARS,
} from './constants'
import { isNullOrUndefined, secondsToDays } from '@pushly/aqe/lib/utils'
import { toRawString } from '../../../_utils/to-raw-string'
import { PromptEditorContextLoadedState } from './context'
import { PromptVisitorConditions } from '@pushly/models/lib/structs/prompts/prompt-visitor-conditions'
import { PromptEditorTreatment } from './types'
import { PromptGroupPageConditions } from '@pushly/models/lib/structs/prompts/prompt-group-page-conditions'
import { UpdatePromptGroupRequest } from '@pushly/models/lib/structs/prompts/update-prompt-group-request'
import { PromptPosition } from '@pushly/models/lib/enums/prompts/prompt-position'

type PromptSource = GetAbstractPromptResponse | CreateAbstractPromptRequest | UpdateAbstractPromptRequest

function validateAbTestDetails(ctx: PromptEditorContextLoadedState) {
    if (ctx.prompts.length === 1) {
        throw new Error('Prompt testing requires at least two tests to be defined.')
    }

    // validate test weights
    const sum = ctx.prompts.map(({ weight }) => weight ?? 0).reduce<number>((n, v) => (n += v), 0)

    if (sum !== 100) {
        throw new Error(`Variant weights must add up to and not exceed 100%. Current total weight is ${sum}%.`)
    }
}

function validatePromptGroupPageConditions<T extends PromptGroupPageConditions>(conditions: T | undefined) {
    if (conditions && Object.keys(conditions).length) {
        if ('pageUrls' in conditions || 'excludedPageUrls' in conditions) {
            const rgx = /^(?:[A-Za-z0-9\-._~:/?#\[\]@!$&'()*+,;=]|\\\\)+$/i

            if (!conditions.pageUrls?.length && !conditions.excludedPageUrls?.length) {
                throw new Error('At least one Page URL or Excluded Page URL must be provided.')
            }

            if (conditions.pageUrls?.length) {
                if (!conditions.pageUrls.every((url) => rgx.test(toRawString(url)))) {
                    throw new Error('One or more Page URLs contain invalid characters.')
                }
            }

            if (conditions.excludedPageUrls?.length) {
                if (!conditions.excludedPageUrls.every((url) => rgx.test(toRawString(url)))) {
                    throw new Error('One or more Excluded Page URLs contain invalid characters.')
                }
            }
        }

        if ('keywords' in conditions) {
            const rgx = /^[A-Za-z0-9\-._~:/?#\[\]@!$&'()*+,;=]+$/i

            if (!conditions.keywords?.length) {
                throw new Error('At least one Meta Keywords must be provided.')
            }

            if (!conditions.keywords.every((url) => rgx.test(toRawString(url)))) {
                throw new Error('One or more Meta Keywords contain invalid characters.')
            }
        }
    }
}

function validatePromptVariants<T extends PromptEditorTreatment>(prompts: T[]) {
    if (!prompts.some((p) => (p.name?.length || 0) <= VARIANT_NAME_MAX_CHARS)) {
        throw new Error('Prompt variant names cannot exceed 45 characters.')
    }
}

function validatePromptBehavior<T extends PromptBehavior>(behavior: T | undefined) {
    if (behavior && !isNullOrUndefined(behavior.cookieLengthSeconds)) {
        const days = secondsToDays(behavior.cookieLengthSeconds)

        if (days < 0) {
            throw new Error('Prompt dismissed time must not be less than 0.')
        }

        if (days > 3650) {
            throw new Error('Prompt dismissed time cannot exceed 3,650 days.')
        }
    }
}

export type VisitorConditionUnit = 'whole seconds' | 'whole percent'

/**
 * Inclusive whole-number range a visitor-condition value must fall within.
 *
 * - `min`: smallest accepted value (inclusive).
 * - `max`: largest accepted value (inclusive). When omitted the value is treated
 *   as having no upper bound (e.g. seconds-based fields).
 * - `unit`: human-readable description of the expected whole-number unit, used in
 *   the "must be in {unit}" error (e.g. "whole seconds", "whole percent").
 */
export type VisitorConditionRange = {
    min: number
    max?: number
    unit: VisitorConditionUnit
}

// Seconds-based fields preserve the original behavior: whole numbers greater than 0.
const SECONDS_VISITOR_CONDITION_RANGE: VisitorConditionRange = { min: 1, unit: 'whole seconds' }

export function validateVisitorCondition<T extends PromptVisitorConditions>(
    conditions: T | undefined,
    key: keyof T,
    description: string,
    range: VisitorConditionRange = SECONDS_VISITOR_CONDITION_RANGE,
) {
    if (conditions && key in conditions) {
        const value = conditions[key]

        if (isNullOrUndefined(value) || !String(value)) {
            throw new Error(`${description} value is required.`)
        }
        if (!/^\d+$/.test(String(value))) {
            throw new Error(`${description} must be in ${range.unit}.`)
        }

        const numericValue = Number(value)
        if (numericValue < range.min || (range.max !== undefined && numericValue > range.max)) {
            throw new Error(
                range.max !== undefined
                    ? `${description} must be between ${range.min} and ${range.max}.`
                    : `${description} must be greater than ${range.min - 1}.`,
            )
        }
    }
}

export function validatePromptGroup(ctx: PromptEditorContextLoadedState) {
    const promptGroup = ctx.promptGroup.clone()
    promptGroup.isActive ??= true

    promptGroup.isAbTest = ctx.isAbTestEnabled
    if (promptGroup.isAbTest) {
        validateAbTestDetails(ctx)
    }

    validatePromptGroupPageConditions(promptGroup.conditions?.page)

    const prompts = ctx.prompts.map((p) => p.clone())
    if (prompts.length === 1) {
        prompts[0].weight = 100
    } else {
        validatePromptVariants(prompts)
    }

    for (const prompt of prompts) {
        // Behaviors
        validatePromptBehavior(prompt.config?.behavior)

        // Visitor Conditions
        validateVisitorCondition(prompt.conditions?.visitor, 'timeOnPageSeconds', 'Time-on-Page')
        validateVisitorCondition(prompt.conditions?.visitor, 'sessionTimeOnSiteSeconds', 'Session Time-on-Site')
        validateVisitorCondition(prompt.conditions?.visitor, 'sessionPageViews', 'Session Page Views')
        validateVisitorCondition(prompt.conditions?.visitor, 'scrollDepthPct', 'Scroll Depth', {
            min: 1,
            max: 100,
            unit: 'whole percent',
        })
    }

    promptGroup.prompts = prompts as typeof promptGroup.prompts

    return promptGroup.validate()
}

// Slide prompt overloads
export function toPrompt(promptClass: typeof CreateSlidePromptRequest, source: PromptSource): CreateSlidePromptRequest
export function toPrompt(
    promptClass: typeof UpdateSlidePromptRequest,
    source: Exclude<PromptSource, CreateSlidePromptRequest>,
): UpdateSlidePromptRequest

// Native prompt overloads
export function toPrompt(promptClass: typeof CreateNativePromptRequest, source: PromptSource): CreateNativePromptRequest
export function toPrompt(
    promptClass: typeof UpdateNativePromptRequest,
    source: Exclude<PromptSource, CreateNativePromptRequest>,
): UpdateNativePromptRequest

// Custom prompt overloads
export function toPrompt(promptClass: typeof CreateCustomPromptRequest, source: PromptSource): CreateCustomPromptRequest
export function toPrompt(
    promptClass: typeof UpdateCustomPromptRequest,
    source: Exclude<PromptSource, CreateCustomPromptRequest>,
): UpdateCustomPromptRequest

// Implementation
export function toPrompt(
    promptClass:
        | typeof CreateSlidePromptRequest
        | typeof UpdateSlidePromptRequest
        | typeof CreateNativePromptRequest
        | typeof UpdateNativePromptRequest
        | typeof CreateCustomPromptRequest
        | typeof UpdateCustomPromptRequest,
    source: PromptSource,
):
    | CreateSlidePromptRequest
    | UpdateSlidePromptRequest
    | CreateNativePromptRequest
    | UpdateNativePromptRequest
    | CreateCustomPromptRequest
    | UpdateCustomPromptRequest {
    const prompt = new promptClass()

    // Set ID for update requests
    if ('id' in source) {
        if (
            isUpdateSlidePromptRequest(prompt) ||
            isUpdateNativePromptRequest(prompt) ||
            isUpdateCustomPromptRequest(prompt)
        ) {
            prompt.id = source.id
        }
    }

    // Set common properties
    prompt.name = source.name
    prompt.isAutoShow = source.isAutoShow
    prompt.weight = source.weight
    prompt.conditions = source.conditions

    // Set style and config based on prompt type
    if (isCreateSlidePromptRequest(prompt) || isUpdateSlidePromptRequest(prompt)) {
        if (
            isGetSlidePromptResponse(source) ||
            isCreateSlidePromptRequest(source) ||
            isUpdateSlidePromptRequest(source)
        ) {
            if (promptClass.name === CreateSlidePromptRequest.name) {
                return CreateSlidePromptRequest.from(source as CreateSlidePromptRequest)
            }
            return UpdateSlidePromptRequest.from(source as UpdateSlidePromptRequest)
        }

        prompt.style = PromptStyle.SLIDE

        prompt.config = SlidePromptConfig.from({
            theme: SlidePromptTheme.from({
                layout: DEFAULT_LAYOUT,
                position: PromptDevicePosition.from({
                    desktop: DEFAULT_DESKTOP_POSITION,
                    mobile: DEFAULT_MOBILE_POSITION,
                }),
                title: PromptTextConfig.from({
                    text: null!,
                    textColor: DEFAULT_TITLE_COLOR,
                }),
                subtitle: PromptTextConfig.from({
                    text: null!,
                    textColor: DEFAULT_SUBTITLE_COLOR,
                }),
                allowButton: PromptButtonConfig.from({
                    text: DEFAULT_ALLOW_TEXT,
                    textColor: DEFAULT_ALLOW_TEXT_COLOR,
                    backgroundColor: DEFAULT_ALLOW_BUTTON_COLOR,
                }),
                dismissButton: PromptButtonConfig.from({
                    text: DEFAULT_DISMISS_TEXT,
                    textColor: DEFAULT_DISMISS_TEXT_COLOR,
                    backgroundColor: DEFAULT_DISMISS_BUTTON_COLOR,
                }),
                backgroundColor: DEFAULT_BACKGROUND_COLOR,
            }),
            behavior: source.config?.behavior ? PromptBehavior.from(source.config.behavior) : new PromptBehavior(),
        })
    } else if (isCreateNativePromptRequest(prompt) || isUpdateNativePromptRequest(prompt)) {
        if (
            isGetNativePromptResponse(source) ||
            isCreateNativePromptRequest(source) ||
            isUpdateNativePromptRequest(source)
        ) {
            if (promptClass.name === CreateNativePromptRequest.name) {
                return CreateNativePromptRequest.from(source as CreateNativePromptRequest)
            }
            return UpdateNativePromptRequest.from(source as UpdateNativePromptRequest)
        }

        prompt.style = PromptStyle.NATIVE
        prompt.config = NativePromptConfig.from({
            theme: new NativePromptTheme(),
            behavior: source.config?.behavior ? PromptBehavior.from(source.config.behavior) : new PromptBehavior(),
        })
    } else if (isCreateCustomPromptRequest(prompt) || isUpdateCustomPromptRequest(prompt)) {
        if (
            isGetCustomPromptResponse(source) ||
            isCreateCustomPromptRequest(source) ||
            isUpdateCustomPromptRequest(source)
        ) {
            if (promptClass.name === CreateCustomPromptRequest.name) {
                return CreateCustomPromptRequest.from(source as CreateCustomPromptRequest)
            }
            return UpdateCustomPromptRequest.from(source as UpdateCustomPromptRequest)
        }

        prompt.style = PromptStyle.PUBLISHER_CUSTOM
        prompt.config = new CustomPromptConfig()
    }

    return prompt
}

/* webpack-strip-block:removed */
